const DEFAULT_ENTRY = {
    enabled: false,
    quantity: '',
    safetyStatus: '',
};

export const getUnconsumedDoseRxKey = (rx = {}) => (
    String(rx?.id ?? rx?.winrx_data_id ?? rx?.rxnumber ?? '')
);

export const getUnconsumedDoseRxQty = (rx = {}) => {
    const rawValue = rx?.refill_reqty ?? rx?.qty ?? rx?.quantity ?? '';
    if (rawValue == null || rawValue === '') return null;

    const parsedValue = Number.parseFloat(String(rawValue).replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getDoseQtyValue = (dose = {}) => {
    const rawValue = dose?.dose ?? dose?.qty ?? dose?.quantity ?? '';
    if (rawValue == null || rawValue === '') return null;

    const parsedValue = Number.parseFloat(String(rawValue).replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getRxDoseList = (rx = {}) => (
    rx?.investigate_dilaudid_delivery_all
    || rx?.investigate_dilaudid_today_missed
    || rx?.investigate_dilaudid_final_missed
    || []
);

const getSelectedDoseIdsForRx = (rx = {}, selectedDoses = {}) => (
    (selectedDoses?.[rx?.rxnumber] || [])
        .map((dose) => Number.parseInt(dose?.dose_id, 10))
        .filter((doseId) => Number.isInteger(doseId) && doseId > 0)
);

const getSelectedDoseQtyForRx = (rx = {}, selectedDoses = {}) => {
    const doseList = getRxDoseList(rx);
    if (!Array.isArray(doseList) || doseList.length === 0) {
        return { qty: null, hasDoseSelectionRule: false };
    }

    const selectedDoseIds = new Set(
        (selectedDoses?.[rx?.rxnumber] || [])
            .map((dose) => String(dose?.dose_id ?? ''))
            .filter(Boolean)
    );

    const selectedQty = doseList
        .filter((dose) => selectedDoseIds.has(String(dose?.id ?? '')))
        .reduce((total, dose) => total + (getDoseQtyValue(dose) || 0), 0);

    return {
        qty: selectedQty,
        hasDoseSelectionRule: true,
    };
};

const parseEnteredQuantity = (value) => {
    const parsedValue = Number.parseFloat(String(value ?? '').trim());
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const getUnconsumedDoseQuantityError = ({
    rx = {},
    selectedDoses = {},
    quantity = '',
}) => {
    const quantityValue = String(quantity ?? '').trim();
    const parsedQuantity = Number.parseFloat(quantityValue);
    const totalQty = getUnconsumedDoseRxQty(rx);
    const { qty: selectedDoseQty, hasDoseSelectionRule } = getSelectedDoseQtyForRx(rx, selectedDoses);
    const maxAllowedQty = hasDoseSelectionRule ? selectedDoseQty : totalQty;

    if (quantityValue === '') {
        return 'Enter quantity.';
    }

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
        return 'Enter valid quantity.';
    }

    if (totalQty !== null && parsedQuantity === totalQty) {
        return 'Cannot be equal to total quantity.';
    }

    if (maxAllowedQty !== null && parsedQuantity > maxAllowedQty) {
        return hasDoseSelectionRule
            ? 'Exceeds selected dose.'
            : 'Exceeds total quantity.';
    }

    return '';
};

export const getSelectedUnconsumedDoseRxs = (rxData = [], selectedDeliverRxIds = []) => {
    if (!Array.isArray(rxData) || rxData.length === 0) return [];

    const selectedIdSet = new Set((selectedDeliverRxIds || []).map((value) => String(value)));
    if (selectedIdSet.size === 0) return [];

    return rxData.filter((rx) => selectedIdSet.has(getUnconsumedDoseRxKey(rx)));
};

export const getDefaultUnconsumedDoseEntry = () => ({ ...DEFAULT_ENTRY });

export const validateUnconsumedDoseEntries = ({
    rxData = [],
    selectedDeliverRxIds = [],
    selectedDoses = {},
    unconsumedDoseEntries = {},
    setUnconsumedDoseErrors = () => { },
}) => {
    const selectedRxs = getSelectedUnconsumedDoseRxs(rxData, selectedDeliverRxIds);
    const nextErrors = {};

    selectedRxs.forEach((rx) => {
        const rxKey = getUnconsumedDoseRxKey(rx);
        const entry = unconsumedDoseEntries?.[rxKey];

        if (!entry?.enabled) return;

        const rxErrors = {};
        const quantityError = getUnconsumedDoseQuantityError({
            rx,
            selectedDoses,
            quantity: entry?.quantity,
        });

        if (quantityError) {
            rxErrors.quantity = quantityError;
        }

        if (!entry?.safetyStatus) {
            rxErrors.safetyStatus = 'Mark dose as reusable or not.';
        }

        if (Object.keys(rxErrors).length > 0) {
            nextErrors[rxKey] = rxErrors;
        }
    });

    setUnconsumedDoseErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
};

export const buildUnconsumedDosePayload = ({
    rxData = [],
    selectedDeliverRxIds = [],
    selectedDoses = {},
    unconsumedDoseEntries = {},
}) => {
    const selectedRxs = getSelectedUnconsumedDoseRxs(rxData, selectedDeliverRxIds);

    return selectedRxs.reduce((payload, rx) => {
        const rxKey = getUnconsumedDoseRxKey(rx);
        const entry = unconsumedDoseEntries?.[rxKey];

        if (!entry?.enabled) {
            return payload;
        }

        const winrxDataId = Number.parseInt(String(rx?.id ?? rx?.winrx_data_id ?? ''), 10);
        const unconsumedQty = parseEnteredQuantity(entry?.quantity);
        const selectedFrequencyDoseIds = getSelectedDoseIdsForRx(rx, selectedDoses);
        const { qty: selectedDoseQty, hasDoseSelectionRule } = getSelectedDoseQtyForRx(rx, selectedDoses);
        const totalQty = getUnconsumedDoseRxQty(rx);
        const effectiveSelectedDoseQty = hasDoseSelectionRule ? selectedDoseQty : totalQty;

        if (!Number.isInteger(winrxDataId) || winrxDataId <= 0 || !Number.isFinite(unconsumedQty)) {
            return payload;
        }

        payload.push({
            winrx_data_id: winrxDataId,
            rx_number: rx?.rxnumber ? String(rx.rxnumber) : '',
            selected_frequency_dose_ids: selectedFrequencyDoseIds,
            selected_dose_qty: effectiveSelectedDoseQty,
            unconsumed_qty: unconsumedQty,
            is_safe_to_reuse: entry?.safetyStatus === 'safe',
        });

        return payload;
    }, []);
};
